import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry, MatIcon } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AidYearChangeService } from '_services/aid-year-change.service';
import { CalcConfigService } from '_services/calc-config.service';
import { SelectorChangesDialogComponent } from '_shared/selector-changes-dialog/selector-changes-dialog.component';
import { AidYearChange } from 'app/_models/calcPackage';
import { Subscription } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import {
  AID_YEARS,
  CURRENT_AID_YEAR,
  formatAidYear,
  isAidYearSupported,
  unsupportedAidYearMessage,
} from './aid-year.config';

interface Option {
  name: string;
  value: number;
  hide: boolean;
}

const AIDYEAR_ICON =
  `
  <svg xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 -960 960 960" width="48"><path d="M142-607v-208h60v106q52-61 123.5-96T477-840q151 0 257 104t106 254v10h-60v-13q0-124-89-209.5T477-780q-68 0-127.5 31T246-667h105v60H142Zm-22 126h60q0 125 86 213t211 88h12l34 57q-11 2-22.5 2.5t-23.5.5q-149 0-253-105.5T120-481Zm440 119L451-469v-214h60v189l81 79-32 53ZM735-10l-10-66q-18-5-34-13.5T661-112l-55 12-25-42 47-44q-3-12-3-25t3-25l-47-44 25-42 55 12q14-14 30-22.5t34-13.5l10-66h54l10 66q18 5 34 13.5t30 22.5l55-12 25 42-47 44q3 12 3 25t-3 25l47 44-25 42-55-12q-14 14-30 22.5T799-76l-10 66h-54Zm27-121q36 0 58-22t22-58q0-36-22-58t-58-22q-36 0-58 22t-22 58q0 36 22 58t58 22Z"/></svg>
`;

@Component({
    selector: 'app-aid-year',
    templateUrl: './aid-year.component.html',
    styleUrls: ['./aid-year.component.css'],
    imports: [MatIconButton, MatTooltip, MatIcon, MatFormField, MatLabel, MatSelect, MatOption, AsyncPipe]
})
export class AidYearComponent implements OnInit, OnDestroy {
  ruleSubscription?: Subscription;
  selectedAidYear = CURRENT_AID_YEAR;
  private warnedUnsupportedAidYear: number | null = null;
  aidYearOptions: Option[] = AID_YEARS.map(year => ({ name: year.label, value: year.code, hide: false }));


  constructor(
    public cc: CalcConfigService,
    private ayc: AidYearChangeService,
    private dialog: MatDialog,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer
  ) {
    iconRegistry.addSvgIconLiteral('aid-year', sanitizer.bypassSecurityTrustHtml(AIDYEAR_ICON));
  }
  @Input() disabled = false;

  ngOnInit() {
    this.ruleSubscription = this.cc.draftRuleInfo.subscribe((draftRuleInfo) => {
      if (draftRuleInfo?.aidYear) {
        // If the aid year is not found in the list of options, add it to the list of options
        if (!this.aidYearOptions.find((option) => option.value === draftRuleInfo.aidYear)) {
          this.aidYearOptions.push({
            name: formatAidYear(draftRuleInfo.aidYear) + '(x)',
            value: draftRuleInfo.aidYear,
            hide: false
          });
        }

        this.selectedAidYear = draftRuleInfo.aidYear;

        if (!isAidYearSupported(draftRuleInfo.aidYear) && this.warnedUnsupportedAidYear !== draftRuleInfo.aidYear) {
          this.warnedUnsupportedAidYear = draftRuleInfo.aidYear;
          alert(unsupportedAidYearMessage(draftRuleInfo.aidYear));
        }

        // Schools on an older year cannot skip ahead to the newest year
        const newest = this.aidYearOptions.find((option) => option.value === AID_YEARS[0].code);
        if (newest) {
          newest.hide = draftRuleInfo.aidYear < AID_YEARS[1].code;
        }


      } else {
        // If rules exist and the aid year is not found, set the aid year to the what is found in the preferences
        // This is a fallback in case the aid year is not found in the draft rule info
        if (draftRuleInfo) { this.ayc.setDraftAidYearFromPreferences(); }
        else { alert('No draft rule info found - contact support'); }
      }
    });
  }

  selectionChange() {
    this.ayc.switchAidYear(this.selectedAidYear);

  }

  openDialog(aidYearChanges: AidYearChange[]) {
    this.dialog.open(SelectorChangesDialogComponent, { data: aidYearChanges });
  }
  ngOnDestroy() {
    this.ruleSubscription?.unsubscribe();
  }
}
